/**
 * 사용자 삭제 확인 모달 컴포넌트
 * - 삭제 확인 메시지 표시
 * - 사용자 정보 표시
 */

import React, { useState } from 'react';
import { Modal, Typography, Space, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';
import type { User } from '../../types/user';
import { getRoleLabel } from '../layout/menuConfig';

const { Text } = Typography;

interface DeleteUserModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);

  /**
   * 사용자 삭제 처리
   */
  const handleDelete = async () => {
    if (!user) return;

    try {
      setLoading(true);
      await userService.deleteUser(user.id);
      message.success('사용자가 삭제되었습니다');
      onClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('사용자 삭제 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          사용자 삭제
        </Space>
      }
      open={open}
      onOk={handleDelete}
      onCancel={onClose}
      confirmLoading={loading}
      okText="삭제"
      cancelText="취소"
      okButtonProps={{ danger: true }}
      width={500}
    >
      {user && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>다음 사용자를 정말 삭제하시겠습니까?</Text>

          <div
            style={{
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '4px',
            }}
          >
            <Space direction="vertical" size="small">
              <div>
                <Text strong>이름:</Text> <Text>{user.name}</Text>
              </div>
              <div>
                <Text strong>이메일:</Text> <Text>{user.email}</Text>
              </div>
              <div>
                <Text strong>역할:</Text> <Text>{getRoleLabel(user.role)}</Text>
              </div>
              {user.group_name && (
                <div>
                  <Text strong>소속 그룹:</Text> <Text>{user.group_name}</Text>
                </div>
              )}
            </Space>
          </div>

          <Text type="danger" strong>
            이 작업은 되돌릴 수 없습니다.
          </Text>
        </Space>
      )}
    </Modal>
  );
};
